// src/pages/Dashboard/Admin/Coaching/components/CoachingEventsTable.jsx

import React from "react";
import { FaEdit, FaTrash, FaCalendarAlt, FaClock } from "react-icons/fa";

import {
  formatDateFR,
  formatHours,
  getEventCoachHour,
} from "../utils/coachingDashboardUtils";

function EventStatusBadge({ hours }) {
  const hasHours = Number(hours || 0) > 0;

  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-bold ring-1 ${
        hasHours
          ? "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20"
          : "bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20"
      }`}
    >
      {hasHours ? "Saisi" : "À compléter"}
    </span>
  );
}

function SkeletonTable() {
  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="space-y-3 p-4 sm:p-5 lg:p-6">
        {[...Array(4)].map((_, index) => (
          <div
            key={index}
            className="h-16 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800"
          />
        ))}
      </div>
    </section>
  );
}

export default function CoachingEventsTable({
  teacher,
  events,
  loading,
  savingDeleteId,
  onEditHours,
  onUnregister,
}) {
  if (loading) return <SkeletonTable />;

  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="p-4 sm:p-5 lg:p-6">
        <div className="mb-4">
          <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
            Événements
          </p>

          <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
            Événements coachés
          </h2>

          <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
            {teacher
              ? `Liste des événements associés à ${teacher.nom} ${teacher.prenom}.`
              : "Liste des événements associés à l’enseignant sélectionné."}
          </p>
        </div>

        {events.length === 0 ? (
          <div className="rounded-[22px] border border-slate-200 bg-slate-50 px-5 py-10 text-center text-sm text-slate-600 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-300">
            Aucun événement trouvé pour cet enseignant.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-[22px] border border-slate-200 dark:border-slate-800">
            <table className="min-w-[900px] w-full">
              <thead className="bg-slate-50 text-xs uppercase tracking-[0.14em] text-slate-500 dark:bg-slate-950/70 dark:text-slate-400">
                <tr>
                  <th className="px-4 py-3 text-left font-bold">Date</th>
                  <th className="px-4 py-3 text-left font-bold">Événement</th>
                  <th className="px-4 py-3 text-left font-bold">Heures</th>
                  <th className="px-4 py-3 text-left font-bold">Statut</th>
                  <th className="sticky right-0 z-10 bg-slate-50 px-4 py-3 text-right font-bold dark:bg-slate-950">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100 text-sm dark:divide-slate-800">
                {events.map((event) => {
                  const hours = getEventCoachHour(event);
                  const eventId = event?.AdherentEvent?.event_id || event?.id;
                  const isDeleting = savingDeleteId === eventId;

                  return (
                    <tr
                      key={`${eventId}-${event?.start || ""}`}
                      className="transition hover:bg-slate-50 dark:hover:bg-slate-800/50"
                    >
                      <td className="px-4 py-4 align-top">
                        <div className="flex items-center gap-2 text-slate-700 dark:text-slate-200">
                          <FaCalendarAlt className="text-indigo-500" />
                          <span className="font-bold">
                            {formatDateFR(event?.start)}
                          </span>
                        </div>
                      </td>

                      <td className="px-4 py-4 align-top">
                        <p className="font-bold text-slate-900 dark:text-white">
                          {event?.titre || "Événement sans titre"}
                        </p>

                        {event?.lieu_event || event?.lieu ? (
                          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                            {event.lieu_event || event.lieu}
                          </p>
                        ) : null}
                      </td>

                      <td className="px-4 py-4 align-top">
                        <div className="flex items-center gap-2 font-bold text-slate-900 dark:text-white">
                          <FaClock className="text-emerald-500" />
                          {formatHours(hours)}
                        </div>
                      </td>

                      <td className="px-4 py-4 align-top">
                        <EventStatusBadge hours={hours} />
                      </td>

                      <td className="sticky right-0 z-10 bg-white px-4 py-4 align-top dark:bg-slate-900">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => onEditHours(event)}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-indigo-200 bg-indigo-50 text-indigo-700 transition hover:bg-indigo-100 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300"
                            title="Modifier les heures"
                          >
                            <FaEdit />
                          </button>

                          <button
                            type="button"
                            disabled={isDeleting}
                            onClick={() => onUnregister(event)}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-rose-200 bg-rose-50 text-rose-700 transition hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300"
                            title="Retirer le coach de l’événement"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}